import React from 'react';
import { makeStyles } from 'tss-react/mui';

import { ListingPhoto } from '@ha/api/v2/types/Listing';
import {
  CameraSettingsRotate1Bold,
  ModernTV3DSyncBold,
  RealEstateDimensionsBlockBold,
  VideoPlayerSliderBold,
} from '@ha/icons';
import { Button, Grid } from '@hbf/dsl';
import { useIntl } from 'ha/i18n';

interface Props {
  onPanoramicPhotosButtonClick: () => void;
  onVideoButtonClick: () => void;
  onFloorplanButtonClick: () => void;
  onVirtualTourButtonClick: () => void;
  numVideos: number;
  floorplanPhotos: ListingPhoto[];
  panoramicPhotos: ListingPhoto[];
  virtualTourLink?: string;
}

const useStyles = makeStyles()(theme => ({
  button: {
    color: theme.palette.white.main,
    background: `linear-gradient(120.33deg, ${theme.palette.grey[500]} 0%, ${theme.palette.white.main} 230.48%)`,
    boxShadow: 'none',
    fontWeight: 'bold',
    textTransform: 'none',
    width: '100%',
    '&:hover': {
      background: `linear-gradient(120.33deg, ${theme.palette.grey[600]} 0%, ${theme.palette.white.main} 230.48%)`,
      boxShadow: 'none',
    },
  },
  mediaSelectorContainer: {
    width: '100%',
    overflowY: 'scroll',
    whiteSpace: 'nowrap',
    marginBottom: theme.spacing(2),
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  mediaSelectorItem: {
    display: 'inline-block',
    marginBottom: theme.spacing(1),
  },
}));

export const GalleryMobileTiles: React.FC<Props> = ({
  onPanoramicPhotosButtonClick,
  onVideoButtonClick,
  onFloorplanButtonClick,
  onVirtualTourButtonClick,
  floorplanPhotos,
  panoramicPhotos,
  numVideos,
  virtualTourLink,
}) => {
  const { classes } = useStyles();
  const { T } = useIntl();

  return (
    <Grid
      container
      wrap="nowrap"
      spacing={1}
      className={classes.mediaSelectorContainer}
    >
      {numVideos > 0 && (
        <Grid item className={classes.mediaSelectorItem}>
          <Button
            variant="contained"
            data-test-locator="Video button"
            className={classes.button}
            size="large"
            onClick={onVideoButtonClick}
            startIcon={<VideoPlayerSliderBold fontSize="inherit" />}
          >
            {T('listings.media.video')}
          </Button>
        </Grid>
      )}
      {floorplanPhotos.length > 0 && (
        <Grid item className={classes.mediaSelectorItem}>
          <Button
            variant="contained"
            data-test-locator="Floor Plan button"
            className={classes.button}
            iconSize="medium"
            size="large"
            onClick={onFloorplanButtonClick}
            startIcon={<RealEstateDimensionsBlockBold fontSize="inherit" />}
          >
            {T('listings.media.floorplan')}
          </Button>
        </Grid>
      )}
      {panoramicPhotos.length > 0 && (
        <Grid item className={classes.mediaSelectorItem}>
          <Button
            variant="contained"
            data-test-locator="Panoramic photo button"
            className={classes.button}
            size="large"
            onClick={onPanoramicPhotosButtonClick}
            startIcon={<CameraSettingsRotate1Bold fontSize="inherit" />}
          >
            {T('360°')}
          </Button>
        </Grid>
      )}
      {virtualTourLink && (
        <Grid item className={classes.mediaSelectorItem}>
          <Button
            variant="contained"
            data-test-locator="Virtual tour button"
            className={classes.button}
            size="large"
            onClick={onVirtualTourButtonClick}
            startIcon={<ModernTV3DSyncBold fontSize="inherit" />}
          >
            {T('listing_gallery_3D_tour')}
          </Button>
        </Grid>
      )}
    </Grid>
  );
};
